import React, { useEffect, useMemo, useState } from 'react';
import { Building, CheckCircle, Download, Eye, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminService, { PendingSubscription } from '../../services/admin.service';
import paymentReceiptsService from '../../services/payment-receipts.service';

const AdminSubscriptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<PendingSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'active' | 'rejected' | ''>('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<PendingSubscription | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    load();
  }, [page, limit, statusFilter]);

  const load = async () => {
    try {
      setLoading(true);
      if (statusFilter === 'pending') {
        const res = await adminService.getPendingSubscriptions(page, limit);
        setSubscriptions(res.subscriptions);
        setTotal(res.total);
      } else {
        const res = await adminService.getSubscriptions(page, limit, statusFilter);
        // Map to PendingSubscription-like shape for table reuse
        const mapped = res.subscriptions.map((s: any) => ({
          id: s.id,
          center: { id: s.trainingCenter.id, name: s.trainingCenter.name, user: { email: '' } },
          subscription_plan: { id: s.plan.id, title: s.plan.name, price: s.plan.price },
          payment_receipt_filename: s.payment_receipt_filename || '',
          status: s.status,
          admin_notes: s.admin_notes,
          created_at: s.createdAt || s.startDate,
        })) as PendingSubscription[];
        setSubscriptions(mapped);
        setTotal(res.total);
      }
    } catch (error: any) {
      // Handle 401 errors specifically to prevent automatic logout
      if (error.response?.status === 401) {
        setNotification({ 
          message: t('admin.subscriptions.errors.sessionExpired'), 
          type: 'error' 
        });
        // Don't clear auth data here, let the user manually refresh or re-login
        // This prevents the automatic logout behavior
      } else {
        setNotification({ 
          message: t('admin.subscriptions.errors.loadError'), 
          type: 'error' 
        });
      }
      
      setSubscriptions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return subscriptions;
    const lower = search.toLowerCase();
    return subscriptions.filter((s) =>
      s.center.name.toLowerCase().includes(lower) ||
      s.subscription_plan.title.toLowerCase().includes(lower)
    );
  }, [subscriptions, search]);

  const approve = async (sub: PendingSubscription) => {
    try {
      setSubmitting(true);
      
      // Check if subscription ID is valid
      if (!sub.id || sub.id === '') {
        throw new Error(t('admin.subscriptions.errors.invalidId'));
      }
      
      await adminService.approveSubscription(sub.id, undefined);
      setSelected(null);
      setNotification({ message: t('admin.subscriptions.messages.approved'), type: 'success' });
      await load();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('admin.subscriptions.errors.approvalError');
      setNotification({ message: `${t('admin.subscriptions.errors.approvalError')}: ${errorMessage}`, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const reject = async (sub: PendingSubscription) => {
    if (!rejectReason.trim()) return;
    try {
      setSubmitting(true);
      await adminService.rejectSubscription(sub.id, rejectReason.trim());
      setSelected(null);
      setRejectReason('');
      setNotification({ message: t('admin.subscriptions.messages.rejected'), type: 'success' });
      await load();
    } catch (error) {
      setNotification({ message: t('admin.subscriptions.errors.rejectionError'), type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD', minimumFractionDigits: 0 }).format(amount);

  const downloadReceipt = async (filename: string) => {
    try {
      const blob = await paymentReceiptsService.downloadReceipt(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setNotification({ message: t('admin.subscriptions.errors.downloadError'), type: 'error' });
    }
  };

  const testConnection = async () => {
    try {
      const response = await adminService.getPendingSubscriptions(1, 1);
      setNotification({ message: t('admin.subscriptions.messages.apiSuccess'), type: 'success' });
    } catch (error) {
      setNotification({ message: t('admin.subscriptions.errors.apiError'), type: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.subscriptions.title')}</h1>
          <p className="text-gray-600 mt-1">{t('admin.subscriptions.subtitle')}</p>
        </div>
      
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t('admin.subscriptions.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-3 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="pending">{t('admin.subscriptions.filters.pending')}</option>
            <option value="active">{t('admin.subscriptions.filters.active')}</option>
            <option value="rejected">{t('admin.subscriptions.filters.rejected')}</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.center')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.plan')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.price')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.receipt')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.subscriptions.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td className="px-6 py-4" colSpan={6}>{t('admin.subscriptions.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td className="px-6 py-4" colSpan={6}>{t('admin.subscriptions.noSubscriptions')}</td></tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                          <Building className="h-5 w-5 text-brand-primary" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{s.center.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{s.subscription_plan.title}</td>
                    <td className="px-6 py-4">{formatCurrency(s.subscription_plan.price)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${s.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.status === 'pending' ? t('admin.subscriptions.status.pending') : s.status === 'active' ? t('admin.subscriptions.status.active') : t('admin.subscriptions.status.rejected')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {s.payment_receipt_filename ? (
                        <button
                          onClick={() => downloadReceipt(s.payment_receipt_filename)}
                          className="text-brand-primary hover:underline inline-flex items-center"
                        >
                          <Download className="h-4 w-4 mr-1" /> {t('admin.subscriptions.download')}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="text-brand-primary hover:opacity-90" onClick={() => setSelected(s)}>
                          <Eye className="h-4 w-4" />
                        </button>
                        {s.status === 'pending' && (
                          <>
                            <button className="text-green-600 hover:text-green-700" onClick={() => approve(s)} disabled={submitting}>
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-700" onClick={() => setSelected(s)}>
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-4 border-t">
          <p className="text-sm text-gray-600">{t('admin.subscriptions.pagination.page', { page, total: Math.max(1, Math.ceil(total / limit)) })}</p>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border rounded" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('admin.subscriptions.pagination.previous')}</button>
            <button className="px-3 py-1 border rounded" disabled={page >= Math.ceil(total / limit)} onClick={() => setPage((p) => p + 1)}>{t('admin.subscriptions.pagination.next')}</button>
            <select className="ml-2 border rounded px-2 py-1" value={limit} onChange={(e) => setLimit(parseInt(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{t('admin.subscriptions.modal.title')}</h2>
              <button onClick={() => { setSelected(null); setRejectReason(''); }} className="text-gray-500">✕</button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                  <Building className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="ml-3">
                  <div className="font-medium">{selected.center.name}</div>
                  <div className="text-sm text-gray-500">{t('admin.subscriptions.modal.planDetails', { plan: selected.subscription_plan.title, price: formatCurrency(selected.subscription_plan.price) })}</div>
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">{t('admin.subscriptions.modal.receipt')}</div>
                {selected.payment_receipt_filename ? (
                  <button
                    onClick={() => downloadReceipt(selected.payment_receipt_filename)}
                    className="text-brand-primary hover:underline inline-flex items-center"
                  >
                    <Download className="h-4 w-4 mr-1" /> {t('admin.subscriptions.modal.downloadReceipt')}
                  </button>
                ) : (
                  <div className="text-gray-400 text-sm">{t('admin.subscriptions.modal.noReceipt')}</div>
                )}
              </div>

              {selected.status === 'pending' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(selected)}
                      disabled={submitting}
                      className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                    >
                      {t('admin.subscriptions.modal.approve')}
                    </button>
                    <button
                      onClick={() => {/* show reject area - already visible below */}}
                      className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      {t('admin.subscriptions.modal.reject')}
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.subscriptions.modal.rejectionReason')}</label>
                    <textarea
                      className="w-full border rounded-lg p-2"
                      rows={3}
                      placeholder={t('admin.subscriptions.modal.rejectionReasonPlaceholder')}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <div className="text-right mt-2">
                      <button
                        onClick={() => reject(selected)}
                        disabled={!rejectReason.trim() || submitting}
                        className="px-4 py-2 rounded border text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-50"
                      >
                        {t('admin.subscriptions.modal.confirmRejection')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">{notification.type === 'success' ? '✓' : '✕'}</span>
            {notification.message}
            <button 
              onClick={() => setNotification(null)} 
              className="ml-4 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionsPage;
